import React from 'react'
import { Cog, RefreshCw, User, Wrench } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { initialsFromEmail } from '@/lib/identity'
import { cn } from '@/lib/utils'
import type { HistoryPerson } from '@/types/history'

/**
 * WHO MADE A CHANGE, AS HISTORY AND ACTIVITY SHOW IT (docs/plans/2026-10-20-change-safety.md §5.6.7).
 *
 * A person is the email latched on the entry when the change was made, shown exactly as sent
 * (R2.4, owner ruling: persons are shown by email). The avatar's letters come from that email
 * through `initialsFromEmail`, the one identity helper; the label is never `displayNameFromEmail`,
 * which would invent a name the person may not have. A source guard test pins that.
 *
 * Automated changes have no person. Their server label picks the icon: the server's three system
 * names are fixed copy (`App::PricingHistory::Presenter.person`), and anything else is a cog.
 */
const SYSTEM_ICONS: Record<string, React.ElementType> = {
  'Your PMS': RefreshCw,
  Automatic: Cog,
  'Maintenance by support': Wrench,
}

export function PersonAvatar({ person, className }: { person: HistoryPerson; className?: string }) {
  const base = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full'

  if (person.kind === 'system') {
    const Icon = SYSTEM_ICONS[person.label] ?? Cog
    return (
      <span className={cn(base, 'bg-muted text-muted-foreground', className)} aria-hidden="true" data-avatar="system">
        <Icon className="h-3.5 w-3.5" />
      </span>
    )
  }

  const initials = person.email ? initialsFromEmail(person.email) : ''
  return (
    <span
      className={cn(
        base,
        'text-[11px] font-semibold',
        // Support wears the dark sidebar tone, so a change made by staff is told apart at a glance.
        person.kind === 'staff' ? 'bg-sidebar text-white' : 'bg-primary/10 text-primary',
        className
      )}
      aria-hidden="true"
      data-avatar={person.kind}
    >
      {initials || <User className="h-3.5 w-3.5" />}
    </span>
  )
}

/** The person's label, and the "Support" badge when the server sends one. */
export default function PersonLabel({ person, className }: { person: HistoryPerson; className?: string }) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5', className)}>
      {/* Wraps, never truncates: "sam@harbour-hot…" would hide whose change it was (R2.4). */}
      <span className="min-w-0 text-[13px] font-semibold text-foreground [overflow-wrap:anywhere]" title={person.label}>
        {person.label}
      </span>
      {person.badge && (
        <Badge className="shrink-0 border-0 bg-teal-50 px-2 py-0 text-[11px] font-semibold text-teal-700 hover:bg-teal-50">
          {person.badge}
        </Badge>
      )}
    </span>
  )
}
